import React from 'react'
import {Group, Image} from 'react-konva'
import useImage from 'use-image'
import transparentPlaceholder from '../../assets/icons/transparent.svg'
import {calcScale} from '../../helpers/shapes/calcScale'

const STATUS = {
    LOADING: 'loading',
    LOADED: 'loaded',
    FAILED: 'failed',
}

/**
 * @typedef {import('./types').ShapeProps & {template: {preview_image?: string}}} TemplateShapeProps
 */

/**
 * Template shape component
 * @param {TemplateShapeProps} props - Shape configuration
 * @returns {import('react').ReactNode}
 */
export const TemplateShape = React.forwardRef((props, ref) => {
    const {size, width, height, template, ...rest} = props || {}
    const x = rest.x
    const y = rest.y

    const {preview_image} = template
    /**
     * @type {[undefined | HTMLImageElement, 'loaded' | 'loading' | 'failed']}
     */
    const [previewSrc, status] = useImage(preview_image, 'Anonymous')
    const isLoadedPreview = status === STATUS.LOADED

    const imageConfig = React.useMemo(() => {
        if (!previewSrc || !isLoadedPreview) {
            return
        }
        const rawSize = {
            width: previewSrc.width,
            height: previewSrc.height,
        }
        if (!size && (!width || !height)) {
            return {
                ...rawSize,
                scaleX: 1,
                scaleY: 1,
            }
        }
        const {scaleX, scaleY} = calcScale(rawSize, size ? size : {width, height}, !!size)
        return {
            ...rawSize,
            scaleX,
            scaleY,
        }
    }, [previewSrc?.width, previewSrc?.height, status])

    const [fallbackSrc] = useImage(transparentPlaceholder, 'Anonymous')

    return (
        <Group x={x} y={y}>
            <Image image={fallbackSrc} />
            {isLoadedPreview && <Image {...imageConfig} ref={ref} image={previewSrc} />}
        </Group>
    )
})

export default TemplateShape
